import { ComplianceTrendData } from "@/interfaces/Charts";
import {
  Diagnosis,
  Driver,
  Fleet,
  ResponseSaveQuestions,
  SaveQuestionsDTO,
  SaveQuestionsForCorporateDTO,
} from "@/interfaces/Company";
import {
  CheckListDTO,
  CreateFastDiagnosis,
  DiagnosisQuestions,
  DiagnosisQuestionsGroup,
  RadarData,
  TotalReport,
  DiagnosisChecklistResponse,
} from "@/interfaces/Diagnosis";
import axiosBaseQuery from "@/utils/axiosBaseQuery";
import { createApi } from "@reduxjs/toolkit/query/react";
import { userService } from "./userService";
import { Notification } from "@/interfaces/Notification";
import { companyService } from "./companyService";
// import { companyService } from "./companyService";

// const { useLoginMutation } = companyService;
// export { useLoginMutation }; // Esta exportación causa un error de TypeScript
/**
 * IMPORTANTE:
 * Debido a una limitación actual, no se pueden exportar los hooks individualmente sin causar problemas de tipos.
 * El siguiente código es un intento de exportar el hook useLoginMutation, pero resulta en el siguiente error de TypeScript:
 * "El tipo inferido de 'useLoginMutation' no se puede nombrar sin una referencia a '../../../node_modules/@reduxjs/toolkit/dist/query/react/buildHooks'. Es probable que esto no sea portátil. Es necesaria una anotación de tipo.ts(2742)"
 * en su lugar llamar directamente a authService y acceder al hook
 * @example
   diagnosisService.findAll(...params)
 */
export const diagnosisService = createApi({
  reducerPath: "diagnosisApi",
  baseQuery: axiosBaseQuery,
  tagTypes: [
    "DiagnosisQuestions",
    "DiagnosisGeneralData",
    "DiagnosisTableReport",
    "DiagnosisChartReport",
    "DiagnosisTotalTableReport",
    "Notification",
  ],
  endpoints: (builder) => ({
    findById: builder.query<
      { diagnosis: Diagnosis },
      { diagnosisId: number; corporate_group?: number }
    >({
      query: ({ diagnosisId, corporate_group }) => ({
        url: `/diagnosis/${diagnosisId}`,
        method: "GET",
        params: {
          corporate_group: corporate_group,
        },
      }),
      providesTags: ["DiagnosisGeneralData"],
    }),
    findQuestionsByCompanySize: builder.query<
      DiagnosisQuestions[],
      { companyId: number }
    >({
      query: ({ companyId }) => ({
        url: `/diagnosis/questions/company-size/?company=${companyId}&group_by_step=true`,
        method: "GET",
      }),
    }),
    findQuestionsByCompanysizeGrouped: builder.query<
      DiagnosisQuestionsGroup[],
      { companyId: number; diagnosisId?: number }
    >({
      query: ({ companyId, diagnosisId }) => ({
        url: `/diagnosis/questions/company-size/?company=${companyId}&diagnosis=${diagnosisId}&group_by_step=true`,
        method: "GET",
      }),
      providesTags: ["DiagnosisQuestions"],
    }),
    radarChart: builder.query<
      RadarData[],
      { companyId: number; diagnosisId?: number }
    >({
      query: ({ companyId, diagnosisId }) => ({
        url: `/diagnosis/radar-chart/?diagnosis=${diagnosisId}&company=${companyId}`,
        method: "GET",
      }),
      transformResponse: (response: { total_table_report?: RadarData[] }) => {
        return response?.total_table_report || [];
      },
      providesTags: ["DiagnosisChartReport"],
    }),
    tableReportTotal: builder.query<
      TotalReport,
      { companyId: number; diagnosisId?: number }
    >({
      query: ({ companyId, diagnosisId }) => ({
        url: `/diagnosis/table-report-total/?diagnosis=${diagnosisId}&company=${companyId}`,
        method: "GET",
      }),
      transformResponse: (response: {
        total_table_report?: TotalReport;
      }): TotalReport => {
        return response?.total_table_report ?? { counts: [], general: 0 };
      },
      providesTags: ["DiagnosisTotalTableReport"],
    }),
    tableReport: builder.query<
      any,
      { companyId: number; diagnosisId?: number }
    >({
      query: ({ companyId, diagnosisId }) => ({
        url: `/diagnosis/table-report/?diagnosis=${diagnosisId}&company=${companyId}`,
        method: "GET",
      }),
      transformResponse: (response: { table_report?: any[] }) => {
        return response?.table_report || [];
      },
      providesTags: ["DiagnosisTableReport"],
    }),
    saveDiagnosis: builder.mutation<DiagnosisChecklistResponse[], CheckListDTO>(
      {
        query: (dataDiagnosis) => ({
          url: `/diagnosis/save-process-checklist/`,
          method: "POST",
          data: {
            ...dataDiagnosis,
          },
        }),
        invalidatesTags: [
          "DiagnosisTableReport",
          "DiagnosisTotalTableReport",
          "DiagnosisChartReport",
          "DiagnosisGeneralData",
        ],
      },
    ),
    generateReport: builder.mutation<
      { file: string },
      {
        // companyId: number;
        format_to_save: string;
        diagnosisId?: number;
      }
    >({
      // query: ({ companyId, format_to_save, diagnosisId }) => ({
      query: ({ format_to_save, diagnosisId }) => ({
        url: `/diagnosis/${diagnosisId}/company/`,
        method: "POST",
        data: {
          // company: companyId,
          format_type: format_to_save,
          // diagnosis: diagnosisId,
        },
      }),
    }),
    generateWorkPlan: builder.mutation<
      { file: string },
      {
        // companyId: number;
        format_to_save: string;
        diagnosisId?: number;
      }
    >({
      // query: ({ companyId, format_to_save, diagnosisId }) => ({
      query: ({ format_to_save, diagnosisId }) => ({
        url: `/diagnosis/${diagnosisId}/workplan/`,
        method: "POST",
        data: {
          // company: companyId,
          format_type: format_to_save,
          // diagnosis: diagnosisId,
        },
      }),
    }),
    findFleetsByCompanyId: builder.query<
      Fleet[],
      { companyId: number; diagnosisId?: number; corporate_group?: number }
    >({
      // query: ({ companyId, diagnosisId, corporate_group }) => ({
      query: ({ diagnosisId }) => ({
        url: `/diagnosis/${diagnosisId}/counter`,
        method: "GET",
        // params: {
        //   company: companyId,
        //   diagnosis: diagnosisId ?? 0,
        //   corporate_group: corporate_group,
        // },
      }),
      transformResponse: (response: {
        diagnosis_count: { fleets_count: Fleet[] };
      }) => response.diagnosis_count.fleets_count,
    }),
    findDriversByCompanyId: builder.query<
      Driver[],
      { companyId: number; diagnosisId?: number; corporate_group?: number }
    >({
      // query: ({ companyId, diagnosisId, corporate_group }) => ({
      query: ({ diagnosisId }) => ({
        url: `/diagnosis/${diagnosisId}/counter`,
        method: "GET",
        // params: {
        //   company: companyId,
        //   diagnosis: diagnosisId ?? 0,
        //   corporate_group: corporate_group,
        // },
      }),
      transformResponse: (response: {
        diagnosis_count: { drivers_count: Driver[] };
      }) => response.diagnosis_count.drivers_count,
    }),
    saveAnswerCuestions: builder.mutation<
      { diagnosis_count: ResponseSaveQuestions },
      SaveQuestionsDTO
    >({
      query: ({ diagnosisId, ...rest }) => ({
        url: `/diagnosis/${diagnosisId}/counter`,
        method: "POST",
        data: {
          ...rest,
        },
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(userService.util.invalidateTags(["FindUserById"]));
        // dispatch(companyService.util.invalidateTags(["GetCompamies"]));
      },
    }),
    initDiagnosisCorporate: builder.mutation<Diagnosis, { corporate: number }>({
      query: ({ corporate }) => ({
        url: `/diagnosis/init_diagnosis_corporate/`,
        method: "POST",
        data: {
          corporate: corporate,
        },
      }),
    }),
    saveCountForCompanyInCorporate: builder.mutation<
      ResponseSaveQuestions,
      SaveQuestionsForCorporateDTO
    >({
      query: (questionsDTO) => ({
        url: `/diagnosis/save_count_for_company_in_corporate/`,
        method: "POST",
        data: {
          ...questionsDTO,
        },
      }),
    }),
    updateDiagnosis: builder.mutation<Diagnosis, Partial<Diagnosis>>({
      query: ({ id, ...rest }) => ({
        url: `/diagnosis/${id}/`,
        method: "PATCH",
        data: {
          ...rest,
        },
      }),
      invalidatesTags: ["DiagnosisQuestions", "DiagnosisGeneralData"],
    }),
    countDiagnosisByConsultor: builder.query<any, { consultor_id: number }>({
      query: ({ consultor_id }) => ({
        url: `/diagnosis/count_diagnosis_by_consultor`,
        method: "GET",
        params: {
          consultor: consultor_id,
        },
      }),
    }),
    countDiagnosisByConsultorByType: builder.query<
      any,
      { consultor_id: number }
    >({
      query: ({ consultor_id }) => ({
        url: `/diagnosis/consultant/${consultor_id}/counter/?type=true`,
        method: "GET",
      }),
    }),
    countDiagnosisByConsultorByModeEjecution: builder.query<
      any,
      { consultor_id?: number }
    >({
      query: ({ consultor_id }) => ({
        url: `/diagnosis/consultant/${consultor_id}/counter/?mode_ejecution=true`,
        method: "GET",
      }),
    }),
    countDiagnosisByConsultors: builder.query<any, void>({
      query: () => ({
        url: `/diagnosis/count/?action=by_consultant`,
        method: "GET",
      }),
    }),
    complianceTrend: builder.query<ComplianceTrendData[], void>({
      query: () => ({
        url: `/diagnosis/count/?action=compliance_trend`,
        method: "GET",
      }),
    }),
    sendReport: builder.mutation<
      string,
      {
        email_to: string;
        diagnosis: number;
        company: number;
        schedule: number;
        sequence: number;
      }
    >({
      query: ({ email_to, diagnosis, company, schedule, sequence }) => ({
        url: `/diagnosis/send-report/`,
        method: "POST",
        data: {
          email_to: email_to,
          diagnosis: diagnosis,
          company: company,
          schedule: schedule,
          sequence: sequence,
        },
      }),
    }),
    findNotificationsByUser: builder.query<
      Notification[] | [],
      {
        user: number;
      }
    >({
      query: ({ user }) => ({
        url: `/diagnosis/find_notifications_by_user`,
        method: "GET",
        params: {
          user: user,
        },
      }),
      providesTags: ["Notification"],
    }),
    readNotifications: builder.mutation<
      { message: boolean },
      {
        notifications: number[];
      }
    >({
      query: ({ notifications }) => ({
        url: `/diagnosis/read_notifications/`,
        method: "PATCH",
        data: {
          notifications: notifications,
        },
      }),
      invalidatesTags: ["Notification"],
    }),
    saveFastDiagnosis: builder.mutation<Diagnosis, CreateFastDiagnosis>({
      query: (createFastDiagnosis) => ({
        url: "/diagnosis/",
        method: "POST",
        data: createFastDiagnosis,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(companyService.util.invalidateTags(["GetCompamies"]));
      },
    }),
  }),
});
